export enum LegalProcedureStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  CLOSED_WON = 'CLOSED_WON',
  CLOSED_LOST = 'CLOSED_LOST',
  SETTLEMENT = 'SETTLEMENT',
}

export enum LegalProcedureType {
  VREDEGERECHT = 'VREDEGERECHT',
  RECHTBANK_EERSTE_AANLEG = 'RECHTBANK_EERSTE_AANLEG',
  HOGER_BEROEP = 'HOGER_BEROEP',
  KORT_GEDING = 'KORT_GEDING',
}

export enum PartyRole {
  EISENDE = 'EISENDE',
  VERWERENDE = 'VERWERENDE',
}

export enum ProcedureDateType {
  // Voorbereidende fase
  PROBLEMEN_DEFINIEREN = 'PROBLEMEN_DEFINIEREN',
  VERZOEKSCHRIFT_OPSTELLEN = 'VERZOEKSCHRIFT_OPSTELLEN',
  STUKKENBUNDEL_SAMENSTELLEN = 'STUKKENBUNDEL_SAMENSTELLEN',
  GEZINSSAMENSTELLING_AANVRAGEN = 'GEZINSSAMENSTELLING_AANVRAGEN',
  VERZOEKSCHRIFT_INDIENEN = 'VERZOEKSCHRIFT_INDIENEN',
  VERZOEKSCHRIFT_DOORSTUREN = 'VERZOEKSCHRIFT_DOORSTUREN',
  // Procedure fase
  DAGVAARDING = 'DAGVAARDING',
  INLEIDINGSZITTING = 'INLEIDINGSZITTING',
  CONCLUSIETERMIJN_EISER = 'CONCLUSIETERMIJN_EISER',
  CONCLUSIETERMIJN_VERWEERDER = 'CONCLUSIETERMIJN_VERWEERDER',
  PLEIDOOI = 'PLEIDOOI',
  SLOTZITTING = 'SLOTZITTING',
  UITSPRAAK = 'UITSPRAAK',
  BETEKENING = 'BETEKENING',
  // Overige
  ZITTING = 'ZITTING',
  ANDERE = 'ANDERE',
}

export interface LegalProcedure {
  id: number;
  rentalContractId: number;
  odooPartnerId?: number;
  contactId?: number;
  companyId?: number;
  status: LegalProcedureStatus;
  type: LegalProcedureType;
  partyRole: PartyRole;
  hasConclusieTermijnen: boolean;
  caseNumber?: string | null;
  court?: string | null;
  lawyerId?: number | null;
  lawyerReference?: string | null;
  claimedAmount?: number | null;
  awardedAmount?: number | null;
  uitvoerbaarVerklaard: boolean;
  issues?: string[] | null;
  remarks?: string | null;
  outcome?: string | null;
  dossiervoorbereidingUserId?: string | null;
  pleitenUserId?: string | null;
  procedureDates: ProcedureDate[];
  rentalContract?: { id: number };
  contact?: { id: number; firstName: string; lastName: string } | null;
  company?: { id: number; name: string } | null;
  lawyer?: { id: number; firstName: string; lastName: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProcedureDate {
  id: number;
  legalProcedureId: number;
  type: ProcedureDateType;
  date: string;
  completed: boolean;
  remarks?: string | null;
  fileLogId?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface LegalProcedureSummary {
  id: number;
  caseNumber: string | null;
  status: LegalProcedureStatus;
  type: LegalProcedureType;
  nextEventType: ProcedureDateType | null;
  nextEventDate: string | null;
}

export const LEGAL_PROCEDURE_STATUS_LABELS: Record<LegalProcedureStatus, string> = {
  [LegalProcedureStatus.PENDING]: 'Voorbereidende fase',
  [LegalProcedureStatus.ACTIVE]: 'Actief',
  [LegalProcedureStatus.SUSPENDED]: 'Opgeschort',
  [LegalProcedureStatus.CLOSED_WON]: 'Gesloten - Gewonnen',
  [LegalProcedureStatus.CLOSED_LOST]: 'Gesloten - Verloren',
  [LegalProcedureStatus.SETTLEMENT]: 'Minnelijke schikking',
};

export const LEGAL_PROCEDURE_STATUS_COLORS: Record<LegalProcedureStatus, string> = {
  [LegalProcedureStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
  [LegalProcedureStatus.ACTIVE]: 'bg-blue-100 text-blue-800',
  [LegalProcedureStatus.SUSPENDED]: 'bg-neutral-100 text-neutral-600',
  [LegalProcedureStatus.CLOSED_WON]: 'bg-green-100 text-green-800',
  [LegalProcedureStatus.CLOSED_LOST]: 'bg-red-100 text-red-800',
  [LegalProcedureStatus.SETTLEMENT]: 'bg-purple-100 text-purple-800',
};

export const LEGAL_PROCEDURE_TYPE_LABELS: Record<LegalProcedureType, string> = {
  [LegalProcedureType.VREDEGERECHT]: 'Vredegerecht',
  [LegalProcedureType.RECHTBANK_EERSTE_AANLEG]: 'Rechtbank eerste aanleg',
  [LegalProcedureType.HOGER_BEROEP]: 'Hoger beroep',
  [LegalProcedureType.KORT_GEDING]: 'Kort geding',
};

export const PARTY_ROLE_LABELS: Record<PartyRole, string> = {
  [PartyRole.EISENDE]: 'Eisende partij',
  [PartyRole.VERWERENDE]: 'Verwerende partij',
};

export const PROCEDURE_DATE_TYPE_LABELS: Record<ProcedureDateType, string> = {
  // Voorbereidende fase
  [ProcedureDateType.PROBLEMEN_DEFINIEREN]: 'Problemen met huurder definiëren',
  [ProcedureDateType.VERZOEKSCHRIFT_OPSTELLEN]: 'Verzoekschrift opstellen',
  [ProcedureDateType.STUKKENBUNDEL_SAMENSTELLEN]: 'Stukkenbundel samenstellen',
  [ProcedureDateType.GEZINSSAMENSTELLING_AANVRAGEN]: 'Attest gezinssamenstelling aanvragen',
  [ProcedureDateType.VERZOEKSCHRIFT_INDIENEN]: 'Verzoekschrift indienen bij rechtbank',
  [ProcedureDateType.VERZOEKSCHRIFT_DOORSTUREN]: 'Verzoekschrift doorsturen aan tegenpartij',
  // Procedure fase
  [ProcedureDateType.DAGVAARDING]: 'Dagvaarding betekend',
  [ProcedureDateType.INLEIDINGSZITTING]: 'Inleidingszitting',
  [ProcedureDateType.CONCLUSIETERMIJN_EISER]: 'Conclusietermijn eiser',
  [ProcedureDateType.CONCLUSIETERMIJN_VERWEERDER]: 'Conclusietermijn verweerder',
  [ProcedureDateType.PLEIDOOI]: 'Pleidooi gepland',
  [ProcedureDateType.SLOTZITTING]: 'Slotzitting',
  [ProcedureDateType.UITSPRAAK]: 'Uitspraak / Vonnis',
  [ProcedureDateType.BETEKENING]: 'Vonnis betekend',
  // Overige
  [ProcedureDateType.ZITTING]: 'Zitting',
  [ProcedureDateType.ANDERE]: 'Andere',
};

/**
 * Predefined issues that can be selected when defining problems with a tenant.
 */
export const PREDEFINED_ISSUES = [
  'Huurachterstand',
  'Overlast',
  'Geluidsoverlast',
  'Laat afval slingeren in het gebouw',
  'Burenruzies',
  'Drugsgebruik',
  'Beschadiging van het gehuurde goed',
  'Ongeoorloofde onderverhuur',
  'Niet-naleving huurcontract',
  'Verwaarlozing van het gehuurde goed',
  'Illegale activiteiten',
  'Huisdieren niet toegelaten',
];

/**
 * Generates the default timeline template for a procedure.
 * Includes preparation phase + legal procedure phase.
 */
export const getDefaultTimelineTemplate = (
  partyRole: PartyRole,
  hasConclusieTermijnen: boolean,
  extraConclusieCount = 0,
): ProcedureDateType[] => {
  const template: ProcedureDateType[] = [
    // Voorbereidende fase
    ProcedureDateType.PROBLEMEN_DEFINIEREN,
    ProcedureDateType.VERZOEKSCHRIFT_OPSTELLEN,
    ProcedureDateType.STUKKENBUNDEL_SAMENSTELLEN,
    ProcedureDateType.GEZINSSAMENSTELLING_AANVRAGEN,
    ProcedureDateType.VERZOEKSCHRIFT_INDIENEN,
    ProcedureDateType.VERZOEKSCHRIFT_DOORSTUREN,
    // Procedure fase
    ProcedureDateType.INLEIDINGSZITTING,
  ];

  if (hasConclusieTermijnen) {
    const totalConclusie = 3 + extraConclusieCount;
    const first =
      partyRole === PartyRole.EISENDE
        ? ProcedureDateType.CONCLUSIETERMIJN_VERWEERDER
        : ProcedureDateType.CONCLUSIETERMIJN_EISER;
    const second =
      partyRole === PartyRole.EISENDE
        ? ProcedureDateType.CONCLUSIETERMIJN_EISER
        : ProcedureDateType.CONCLUSIETERMIJN_VERWEERDER;

    for (let i = 0; i < totalConclusie; i++) {
      template.push(i % 2 === 0 ? first : second);
    }
  }

  template.push(
    ProcedureDateType.SLOTZITTING,
    ProcedureDateType.UITSPRAAK,
    ProcedureDateType.BETEKENING,
  );

  return template;
};
